import { useState } from "react";
import { Camera } from "lucide-react";
import type { DryRun, Job, JobWorkflow } from "@clevis/shared-types";
import { Button, Input, cn } from "@clevis/shared-ui";
import { call } from "../lib/callFunction";

// The job's next step, one button at a time, as its type's workflow lays it
// out: On my way → I'm here → Finish (or Partial complete / Dry run). Each
// press goes through advanceJob, so the office sees the same story.

const btn = "px-3 py-1 text-xs";

export function JobSteps({
  tenantId,
  jobId,
  job,
  workflow,
  signoffRequired,
  photos,
  onTakePhoto,
  onNote,
}: {
  tenantId: string;
  jobId: string;
  job: Job;
  workflow: JobWorkflow;
  signoffRequired: boolean;
  /** The job's photos, newest first (a dry run for the rep needs one from today). */
  photos: Array<{ id: string; url?: string; createdAt: number }>;
  onTakePhoto: () => void;
  /** What the server said after a step ("Customer texted…"). */
  onNote: (note: string) => void;
}) {
  const [form, setForm] = useState<"partial" | "dry_run" | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");
  const needSignoff = signoffRequired && !job.signedOffAt;
  const todayPhoto = photos.find((p) => p.createdAt > Date.now() - 3 * 3600_000) ?? null;

  async function step(name: "on_the_way" | "arrive" | "complete" | "partial" | "dry_run" | "reopen") {
    setBusy(name);
    setError("");
    try {
      const r = await call<{ note: string }>("advanceJob", { tenantId, jobId, step: name, reason: reason.trim(), photoId: name === "dry_run" ? (todayPhoto?.id ?? null) : null });
      onNote(r.note);
      setForm(null);
      setReason("");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  const primary =
    job.status === "scheduled"
      ? workflow.onTheWay && !job.enRouteAt
        ? { step: "on_the_way" as const, label: "On my way" }
        : { step: "arrive" as const, label: "I'm here" }
      : job.status === "in_progress"
        ? { step: "complete" as const, label: "Finish job" }
        : null;
  const secondary: Array<{ step: "arrive" | "partial" | "dry_run" | "reopen"; label: string }> = [];
  if (job.status === "scheduled" && primary?.step === "on_the_way") secondary.push({ step: "arrive", label: "I'm here (skip the text)" });
  if (job.status === "in_progress") {
    if (workflow.partial) secondary.push({ step: "partial", label: "Partial complete" });
    if (workflow.dryRun !== "off") secondary.push({ step: "dry_run", label: "Dry run" });
  }
  if (job.status === "done") secondary.push({ step: "reopen", label: "Reopen" });

  return (
    <div className="flex flex-col items-end gap-1.5">
      {primary ? (
        <Button
          className={btn}
          busy={busy === primary.step}
          disabled={busy !== null || (primary.step === "complete" && needSignoff)}
          title={primary.step === "complete" && needSignoff ? "Get the customer's sign-off first" : undefined}
          onClick={() => void step(primary.step)}
        >
          {primary.label}
        </Button>
      ) : null}
      {primary?.step === "complete" && needSignoff ? <span className="text-[11px] text-brandorange-dark">Customer sign-off first</span> : null}
      {secondary.length ? (
        <div className="flex flex-wrap justify-end gap-x-3 gap-y-0.5 text-xs">
          {secondary.map((s) => (
            <button
              key={s.step}
              className={cn("underline hover:text-ink", form === s.step ? "text-ink" : "text-steel")}
              disabled={busy !== null}
              onClick={() => {
                if (s.step === "partial" || s.step === "dry_run") {
                  setForm(form === s.step ? null : s.step);
                  setError("");
                } else void step(s.step);
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      ) : null}
      {form ? (
        <form
          className="w-72 max-w-full space-y-2 rounded-lg border border-steel/20 bg-cream/50 p-2 text-left"
          onSubmit={(e) => {
            e.preventDefault();
            void step(form);
          }}
        >
          <p className="text-xs font-semibold text-ink">{form === "partial" ? "Partial complete: what's left to do?" : "Dry run: why couldn't the work be done?"}</p>
          <Input className="py-1 text-sm" autoFocus value={reason} maxLength={1000} placeholder={form === "partial" ? "e.g. Opener install; door is up" : "e.g. Nobody home; no answer at the door"} onChange={(e) => setReason(e.target.value)} />
          {form === "dry_run" && workflow.dryRun === "photo_rep" ? (
            <div className="flex items-center gap-2 text-xs">
              {todayPhoto?.url ? <img src={todayPhoto.url} alt="" className="h-10 w-10 rounded object-cover" /> : null}
              <span className={todayPhoto ? "text-success" : "text-brandorange-dark"}>{todayPhoto ? "Photo attached for the rep" : "Take a photo at the door for the rep"}</span>
              <button type="button" className="ml-auto flex items-center gap-1 text-steel underline hover:text-ink" onClick={onTakePhoto}>
                <Camera size={13} /> {todayPhoto ? "Retake" : "Take photo"}
              </button>
            </div>
          ) : null}
          <p className="text-[11px] text-steel">
            {form === "partial"
              ? "This job is done for today. A return visit for the rest goes to dispatch to book."
              : workflow.dryRun === "office_reschedule"
                ? "The office gets a to-do to contact the customer and reschedule."
                : workflow.dryRun === "photo_rep"
                  ? "The office gets a to-do to send the rep your photo and reschedule."
                  : "The job goes back to dispatch for a new date."}
          </p>
          <div className="flex gap-2">
            <Button type="submit" className={btn} busy={busy === form} disabled={reason.trim().length < 2 || (form === "dry_run" && workflow.dryRun === "photo_rep" && !todayPhoto)}>
              {form === "partial" ? "Done for today" : "Log the dry run"}
            </Button>
            <Button type="button" variant="ghost" className={btn} onClick={() => setForm(null)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
      {error ? <p className="max-w-72 text-right text-[11px] text-danger">{error}</p> : null}
    </div>
  );
}

/** "Dry run ×2 · Nobody home" for a job's header. */
export function dryRunLine(job: Job): string {
  const runs = (job.dryRuns ?? []) as DryRun[];
  if (!runs.length) return "";
  const last = runs[runs.length - 1]!;
  return `Dry run${runs.length > 1 ? ` ×${runs.length}` : ""} · ${last.reason}`;
}
